import { PageMain, UserAvatar } from "components/molecules";
import React from "react";
import {
  HiMail,
  HiPhone,
  HiIdentification,
  HiCalendar,
  HiLocationMarker,
  HiOfficeBuilding,
  HiChevronDown,
} from "react-icons/hi";

import { useCourse } from "api/hooks";
import { useStudent } from "api/hooks/useStudent";
import { StudentApi } from "api/studentsApi";
import { useStudentCourses } from "api/student-courses";
import { IconClipboardText, IconClipboardTextProps, PaziaBox, PaziaBreadcrumbs, MoreActionsMenu } from "components/atoms";
import { ClipboardText } from "components/atoms/ClipboardText/ClipboardText";
import { Button } from "components/ui/button";
import { ArchiveUserModal } from "components/templates/ArchiveUserModal/ArchiveUserModal";
import { BlockUserModal } from "components/templates/BlockUserModal/BlockUserModal";
import { routes } from "consts";
import { useNavigate, useParams } from "react-router-dom";
import { PaziaLoader } from "components/atoms";
import { formatDate } from "utils";
import { AddStudentToCourseModal } from "./AddStudentToCourseModal";
import { ChangeEmailModal } from "./ChangeEmailModal";
import { StudentCourses } from "./StudentCourses";
import { StudentNotes } from "./StudentNotes";
import { StudentPanels } from "./StudentPanels";

export const StudentPage = () => {
  const { studentId, courseId } = useParams();
  const [blockUser, setBlockUser] = React.useState<StudentApi | null>(null);
  const navigate = useNavigate();
  const [archiveUser, setArchiveUser] = React.useState<StudentApi | null>(null);
  const [addToCourse, setAddToCourse] = React.useState(false);
  const [changeEmailStudent, setChangeEmailStudent] = React.useState<StudentApi | null>(null);
  const [contactOpen, setContactOpen] = React.useState(false);
  const { loading, data } = useStudent(Number(studentId));
  const { loading: loadingCourse, data: course } = useCourse(Number(courseId));
  const { courses: studentCourses } = useStudentCourses(Number(studentId));

  if (loading || loadingCourse) return <PaziaLoader position="center" />;
  if (!data) return null;

  const {
    licencePhoto,
    firstName,
    surname,
    pesel,
    email,
    phone,
    address,
    bornDate,
    bornPlace,
    buyer,
    buyerAddress,
    buyerNIP,
  } = data;

  const totalCourses = studentCourses.length;
  const paidCourses = studentCourses.filter((c) => c.fullyPaid).length;

  const handleSendEmail = () => navigate(`${routes.EMAILS}${routes.SCHEDULED_EMAILS_CREATE}?recipients=${studentId}`);
  const handleSendSMS = () => navigate(`${routes.EMAILS}${routes.SCHEDULED_MESSAGES_CREATE}?recipients=${studentId}`);

  return (
    <PageMain>
      {/* Breadcrumbs */}
      <div className="mb-6">
        <PaziaBreadcrumbs
          segments={
            courseId && course
              ? [
                  { title: "Kursy", path: routes.COURSES },
                  { title: course.data.name, path: routes.COURSES_MANAGE(courseId) },
                  { title: "Kursanci", path: routes.COURSES_MANAGE_NESTED.STUDENTS(courseId) },
                  { title: firstName + " " + surname },
                ]
              : [{ title: "Użytkownicy", path: routes.STUDENTS }, { title: firstName + " " + surname }]
          }
        />
      </div>

      {/* Hero */}
      <PaziaBox className="mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-5">
          <div className="flex items-center gap-4">
            <UserAvatar avatar={licencePhoto} size="smallMedium" firstName={firstName} surname={surname} />
            <div className="min-w-0 flex-1 md:hidden">
              <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                {firstName} {surname}
              </h1>
              {totalCourses > 0 && (
                <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                  Zapisany na {totalCourses} {totalCourses === 1 ? "kurs" : "kursów"} · {paidCourses} opłaconych
                </p>
              )}
            </div>
          </div>
          <div className="hidden min-w-0 flex-1 md:block">
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              {firstName} {surname}
            </h1>
            {totalCourses > 0 && (
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                Zapisany na {totalCourses} {totalCourses === 1 ? "kurs" : "kursów"} · {paidCourses} opłaconych
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 md:flex-shrink-0">
            <Button type="button" variant="default" onClick={handleSendEmail} className="flex-1 md:flex-initial">
              <HiMail size={16} />
              Wyślij email
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSendSMS}
              disabled={!phone}
              className="flex-1 md:flex-initial"
            >
              <HiPhone size={16} />
              SMS
            </Button>
            <MoreActionsMenu
              type="tertiary"
              actions={[
                { name: "Dodaj do kursu", onClick: () => setAddToCourse(true) },
                { name: "Zmień email", onClick: () => setChangeEmailStudent(data) },
                { name: "Edytuj profil", onClick: () => navigate(routes.STUDENT_EDIT(Number(studentId))) },
                { name: "Zablokuj", onClick: () => setBlockUser(data) },
                { name: "Usuń kursanta", onClick: () => setArchiveUser(data) },
              ]}
            />
          </div>
        </div>

        {/* Desktop: contact facts always visible */}
        <div className="mt-4 hidden flex-wrap gap-x-7 gap-y-2 border-t border-slate-200 pt-4 md:flex dark:border-slate-700">
          <StudentContactFacts
            email={email}
            phone={phone}
            pesel={pesel}
            bornDate={bornDate}
            bornPlace={bornPlace}
            address={address}
            buyer={buyer}
            buyerAddress={buyerAddress}
            buyerNIP={buyerNIP}
            onEditProfile={() => navigate(routes.STUDENT_EDIT(Number(studentId)))}
          />
        </div>

        {/* Mobile: contact facts collapsible */}
        <div className="mt-4 border-t border-slate-200 pt-3 md:hidden dark:border-slate-700">
          <button
            type="button"
            onClick={() => setContactOpen((v) => !v)}
            className="flex w-full items-center justify-between text-sm font-semibold text-slate-600 dark:text-slate-300"
          >
            Dane kontaktowe
            <HiChevronDown className={`text-slate-400 transition-transform ${contactOpen ? "rotate-180" : ""}`} size={18} />
          </button>
          {contactOpen && (
            <div className="mt-3 flex flex-col gap-2">
              <StudentContactFacts
                email={email}
                phone={phone}
                pesel={pesel}
                bornDate={bornDate}
                bornPlace={bornPlace}
                address={address}
                buyer={buyer}
                buyerAddress={buyerAddress}
                buyerNIP={buyerNIP}
                onEditProfile={() => navigate(routes.STUDENT_EDIT(Number(studentId)))}
              />
            </div>
          )}
        </div>
      </PaziaBox>

      {/* Master-detail: course list + notes | course detail */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        <PaziaBox className="order-1 col-span-12 overflow-hidden p-0 md:col-span-4">
          <StudentCourses studentId={Number(studentId)} />
        </PaziaBox>
        <StudentPanels studentId={Number(studentId)} courseId={Number(courseId)} className="order-2 md:order-2" />
        <StudentNotes studentId={Number(studentId)} inline className="order-3 col-span-12 md:order-3 md:col-span-4" />
      </div>

      {/* Modals */}
      <BlockUserModal user={blockUser} onCloseRequest={() => setBlockUser(null)} />
      <ArchiveUserModal
        user={archiveUser}
        onCloseRequest={() => setArchiveUser(null)}
        onSuccessArchive={() => navigate(routes.STUDENTS)}
      />
      <AddStudentToCourseModal
        studentId={Number(studentId)}
        isOpen={addToCourse}
        onCloseRequest={() => setAddToCourse(false)}
        onSuccess={() => setAddToCourse(false)}
      />
      <ChangeEmailModal
        student={changeEmailStudent}
        onCloseRequest={() => setChangeEmailStudent(null)}
        onSuccess={() => {
          setChangeEmailStudent(null);
        }}
      />
    </PageMain>
  );
};

interface StudentContactFactsProps {
  email: string;
  phone: string;
  pesel: string;
  bornDate?: Date;
  bornPlace?: string;
  address: string;
  buyer: string;
  buyerAddress: string;
  buyerNIP: string;
  onEditProfile: () => void;
}

const StudentContactFacts = ({
  email,
  phone,
  pesel,
  bornDate,
  bornPlace,
  address,
  buyer,
  buyerAddress,
  buyerNIP,
  onEditProfile,
}: StudentContactFactsProps) => (
  <>
    {email && <StudentIconClipboardText text={email} icon={<HiMail />} />}
    {phone && <StudentIconClipboardText text={phone} displayText={`Tel: ${phone}`} icon={<HiPhone />} />}
    {pesel && <StudentIconClipboardText text={pesel} displayText={`PESEL: ${pesel}`} icon={<HiIdentification />} />}
    {bornDate && (
      <StudentIconClipboardText
        text={formatDate(bornDate, false, false)}
        displayText={`Ur. ${formatDate(bornDate, false, false)}`}
        icon={<HiCalendar />}
      />
    )}
    {bornPlace && (
      <StudentIconClipboardText text={bornPlace} displayText={`Miejsce ur.: ${bornPlace}`} icon={<HiLocationMarker />} />
    )}
    {address && <StudentIconClipboardText text={address} icon={<HiLocationMarker />} />}
    <div className="flex items-center">
      <HiOfficeBuilding />
      {buyer || buyerAddress || buyerNIP ? (
        <span className="ml-2 whitespace-nowrap text-sm font-medium text-slate-500 dark:text-slate-400">
          {[buyer, buyerNIP && `NIP: ${buyerNIP}`, buyerAddress].filter(Boolean).join(" · ")}
        </span>
      ) : (
        <span className="ml-2 text-sm italic text-slate-400 dark:text-slate-500">
          Brak danych nabywcy —{" "}
          <button className="underline hover:text-slate-600 dark:hover:text-slate-300" onClick={onEditProfile} type="button">
            uzupełnij
          </button>
        </span>
      )}
    </div>
  </>
);

export const StudentIconClipboardText = (props: IconClipboardTextProps & { displayText?: string }) => {
  if (!props.text)
    return (
      <div className="flex items-center">
        {props.icon} <span className="ml-2 text-sm italic text-slate-400 dark:text-slate-300">nie uzupełniono</span>
      </div>
    );
  return (
    <div className="flex min-w-0 items-center">
      {props.icon}
      <ClipboardText
        text={props.text}
        displayText={props.displayText}
        className="ml-2 min-w-0 text-sm [overflow-wrap:anywhere] font-medium text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
      />
    </div>
  );
};
